import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';
import UserContainer from '#components/blocks/user/UserContainer';
import ProfileList from '#modules/profileSelection/profilelist/ProfileList';
import UserName from '#components/blocks/userName/UserName';
import AvatarList from '#modules/profileSelection/avatarlist/AvatarList';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import { UserProfile } from '#types';
import {
  useProfileNavigation,
  ProfileViews,
} from '#modules/profileSelection/useProfileNavigationContext';

const DialogDeleteProfile = dynamic(
  () => import('#modules/dialog-delete-profile/DialogDeleteProfile'),
  {
    ssr: false,
  }
);

enum Views {
  CHOOSE_PROFILE,
  CHOOSE_AVATAR,
  CHOOSE_NAME,
}

const EditProfileView = () => {
  const { t } = useTranslation();
  const { userProfiles, profile } = useOvermindState().user;
  const { updateProfile, deleteProfile } = useOvermindActions().user;
  const { setProfileView } = useProfileNavigation();
  const [view, setView] = useState(Views.CHOOSE_PROFILE);
  const [selectedProfileId, setSelectedProfileId] = useState<
    number | undefined
  >();
  const [userName, setUserName] = useState<string | undefined>();
  const [avatarAssetId, setAvatarAssetId] = useState<number | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteProfile = (userProfile: UserProfile) => {
    setSelectedProfileId(userProfile.id);
    setAvatarAssetId(userProfile.avatarAssetId);
    setUserName(userProfile.name);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfiirmDelete = () => {
    setShowDeleteModal(false);
    deleteProfile(selectedProfileId);
    setProfileView(ProfileViews.SELECT_PROFILE);
  };

  const handleEditProfile = (profileToSelect: UserProfile) => {
    setSelectedProfileId(profileToSelect.id);
    setAvatarAssetId(profileToSelect.avatarAssetId);
    setUserName(profileToSelect.name);
    setView(Views.CHOOSE_AVATAR);
  };

  const handleSelectAvatar = (id: number) => {
    setAvatarAssetId(id);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setUserName(value);
  };

  const handleUpdateProfile = () => {
    const userProfile = userProfiles.find(
      (entry) => entry.id === selectedProfileId
    );
    if (userName && avatarAssetId) {
      updateProfile({ id: userProfile.id, name: userName, avatarAssetId });
    }
    setProfileView(ProfileViews.SELECT_PROFILE);
  };

  const handleAvatarSelectEnterKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setView(Views.CHOOSE_NAME);
    }
  };

  const handleEditProfileEnterKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUpdateProfile();
    }
  };

  const handleBackToProfileSelection = () => {
    setProfileView(ProfileViews.SELECT_PROFILE);
  };

  if (view === Views.CHOOSE_PROFILE) {
    return (
      <UserContainer
        title={t('editProfileTitle')}
        secondaryButtonText={t('globalButtonsDone')}
        onSecondaryClick={handleBackToProfileSelection}
        onClose={handleBackToProfileSelection}
      >
        <div className="tw-mt-auto tw-mb-8">
          <ProfileList
            title={t('chooseUser')}
            profiles={userProfiles}
            mode={'edit'}
            selectedProfileId={profile?.id}
            onDelete={handleDeleteProfile}
            onItemClick={handleEditProfile}
          />
        </div>
        <DialogDeleteProfile
          open={showDeleteModal}
          avatarId={avatarAssetId}
          profileName={userName}
          onConfirm={handleConfiirmDelete}
          onCancel={handleCancelDelete}
        />
      </UserContainer>
    );
  }

  if (view === Views.CHOOSE_AVATAR) {
    return (
      <UserContainer
        title={t('editProfileTitle')}
        subtitle={t('chooseAvatar')}
        onClose={handleBackToProfileSelection}
        secondaryButtonText={t('globalButtonsCancel')}
        onSecondaryClick={handleBackToProfileSelection}
        primaryButtonText={t('editProfileChangeUsername', null, {
          fallback: 'Benutzername ändern',
        })}
        onPrimaryClick={() => setView(Views.CHOOSE_NAME)}
      >
        <AvatarList
          title={t('chooseUser')}
          onKeyPress={handleAvatarSelectEnterKey}
          onSelect={handleSelectAvatar}
          selectedAvatarAssetId={avatarAssetId}
        />
      </UserContainer>
    );
  }

  if (view === Views.CHOOSE_NAME) {
    return (
      <UserContainer
        title={t('editProfileTitle')}
        subtitle={t('enterProfileName')}
        onClose={handleBackToProfileSelection}
        secondaryButtonText={t('globalButtonsCancel')}
        primaryButtonText={t('globalButtonsSave')}
        onPrimaryClick={handleUpdateProfile}
        onSecondaryClick={handleBackToProfileSelection}
        isSubmitDisabled={!userName}
      >
        <UserName
          placeholder={t('chooseProfileName')}
          avatarAssetId={avatarAssetId}
          value={userName}
          onChangeName={handleNameChange}
          onKeyPress={handleEditProfileEnterKey}
        />
      </UserContainer>
    );
  }
};

export default EditProfileView;
